import IsolatedLexicalEditor from './IsolatedLexicalEditor'
import Input from './Input'
import EntitySelector from './EntitySelector'
import FormSelector from './FormSelector'
import ImageUpload from './ImageUpload'
import { memo, useCallback, useMemo } from 'react'
import { GripVertical } from 'lucide-react'
import { getBackendUrl } from '../utils/backendUrl'

const getFullImageUrl = (url) => {
  if (!url) return ''
  if (typeof url !== 'string') {
    if (typeof url === 'object' && url.url) return getFullImageUrl(url.url)
    return ''
  }
  if (url.startsWith('http') || url.startsWith('data:')) return url
  const backendUrl = getBackendUrl()
  return backendUrl ? `${backendUrl}${url}` : url
}
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const getEmptyLexicalJSON = () => ({
  root: {
    children: [
      {
        children: [],
        direction: 'ltr',
        format: '',
        indent: 0,
        type: 'paragraph',
        version: 1,
      },
    ],
    direction: 'ltr',
    format: '',
    indent: 0,
    type: 'root',
    version: 1,
  },
})

function SortableMultiselectItem({ value, index, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `multiselect-${index}-${value}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing mr-1"
      >
        <GripVertical className="h-3 w-3" />
      </div>
      <span>{value}</span>
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="ml-2 text-blue-600 hover:text-blue-800"
      >
        ×
      </button>
    </div>
  )
}

/**
 * @typedef {Object} InlineFieldEditorProps
 * @property {Object} field - The field configuration
 * @property {*} value - The current field value
 * @property {Function} onChange - Callback when field value changes
 * @property {boolean} isEditable - Whether the field should be editable
 * @property {string} locale - The current locale
 * @property {string} blockId - The block ID for unique key generation
 * @property {string} blockTypeId - The block type ID for unique key generation
 * @property {boolean} readOnly - Whether the field is in read-only mode
 */

/**
 * @param {InlineFieldEditorProps} props
 */
const InlineFieldEditor = memo(
  function InlineFieldEditor({
    field,
    value,
    onChange,
    isEditable = false,
    locale = 'en',
    blockId,
    blockTypeId,
    readOnly = false,
  }) {
    const uniqueEditorId = useMemo(
      () => `${blockTypeId}-${blockId}-${field.id}`,
      [blockTypeId, blockId, field.id]
    )

    const lexicalValue = useMemo(() => {
      if (
        !value ||
        (typeof value === 'object' && Object.keys(value).length === 0)
      ) {
        return getEmptyLexicalJSON()
      }
      return typeof value === 'object' ? value : getEmptyLexicalJSON()
    }, [value])

    const handleLexicalChange = useCallback(
      (lexicalJSON) => {
        onChange(lexicalJSON)
      },
      [onChange]
    )

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    )

    const renderFieldInput = () => {
      switch (field.type?.toLowerCase()) {
        case 'text':
          return (
            <Input
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={
                field.placeholder || `Enter ${field.label.toLowerCase()}`
              }
              className="w-full"
            />
          )

        case 'textarea':
          return (
            <textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={
                field.placeholder || `Enter ${field.label.toLowerCase()}`
              }
              rows={3}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          )

        case 'entity selector':
          return (
            <EntitySelector
              field={field}
              value={value}
              onChange={onChange}
              locale={locale}
              readOnly={!isEditable}
            />
          )

        case 'form selector':
          return (
            <FormSelector
              value={value}
              onChange={onChange}
              readOnly={!isEditable}
            />
          )

        case 'rich text':
          return (
            <div data-editor-id={uniqueEditorId}>
              <IsolatedLexicalEditor
                initialJSON={lexicalValue}
                onChange={handleLexicalChange}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                editorKey={uniqueEditorId}
                readOnly={false}
              />
            </div>
          )

        case 'number':
          return (
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={
                field.placeholder || `Enter ${field.label.toLowerCase()}`
              }
              className="w-full"
            />
          )

        case 'boolean':
          return (
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                {Boolean(value) ? 'Yes' : 'No'}
              </span>
            </label>
          )

        case 'file':
          return <ImageUpload value={value || ''} onChange={onChange} />

        case 'url': {
          const urlValue =
            typeof value === 'object'
              ? value
              : { text: '', url: value || '', target_blank: false }
          return (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Anchor Text
                </label>
                <Input
                  type="text"
                  value={urlValue.text || ''}
                  onChange={(e) =>
                    onChange({ ...urlValue, text: e.target.value })
                  }
                  placeholder="Link text"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  URL
                </label>
                <Input
                  type="url"
                  value={urlValue.url || ''}
                  onChange={(e) =>
                    onChange({ ...urlValue, url: e.target.value })
                  }
                  placeholder={field.placeholder || 'https://example.com'}
                  className="w-full"
                />
              </div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={Boolean(urlValue.target_blank)}
                  onChange={(e) =>
                    onChange({ ...urlValue, target_blank: e.target.checked })
                  }
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Open in new tab</span>
              </label>
            </div>
          )
        }

        case 'email':
          return (
            <Input
              type="email"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={
                field.placeholder || `Enter ${field.label.toLowerCase()}`
              }
              className="w-full"
            />
          )

        case 'date':
          return (
            <Input
              type="date"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full"
            />
          )

        case 'color':
          return (
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={value || '#000000'}
                onChange={(e) => onChange(e.target.value)}
                className="h-10 w-20 rounded border-gray-300"
              />
              <Input
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Hex color"
                className="flex-1"
              />
            </div>
          )

        case 'select':
          return (
            <select
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Select {field.label.toLowerCase()}</option>
              {field.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )

        case 'multiselect': {
          const currentValues = value || []

          const handleRemove = (index) => {
            onChange(currentValues.filter((_, i) => i !== index))
          }

          const handleDragEnd = (event) => {
            const { active, over } = event

            if (!over || active.id === over.id) {
              return
            }

            const oldIndex = parseInt(active.id.split('-')[1])
            const newIndex = parseInt(over.id.split('-')[1])

            onChange(arrayMove(currentValues, oldIndex, newIndex))
          }

          return (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2 mb-3">
                {currentValues.length > 0 && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={currentValues.map(
                        (val, idx) => `multiselect-${idx}-${val}`
                      )}
                    >
                      {currentValues.map((val, index) => (
                        <SortableMultiselectItem
                          key={`${val}-${index}`}
                          value={val}
                          index={index}
                          onRemove={handleRemove}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
              <div className="space-y-2">
                {field.options?.map((option) => (
                  <label key={option} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={currentValues.includes(option)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange([...currentValues, option])
                        } else {
                          onChange(currentValues.filter((v) => v !== option))
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        }

        case 'repeater':
          return (
            <div className="space-y-2">
              {(value || []).map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Input
                    value={item || ''}
                    onChange={(e) => {
                      const newValue = [...(value || [])]
                      newValue[index] = e.target.value
                      onChange(newValue)
                    }}
                    placeholder={`Item ${index + 1}`}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newValue = [...(value || [])]
                      newValue.splice(index, 1)
                      onChange(newValue)
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  onChange([...(value || []), ''])
                }}
                className="text-primary-600 hover:text-primary-800"
              >
                Add Item
              </button>
            </div>
          )

        default:
          return (
            <div className="text-sm text-gray-500">
              Unsupported field type: {field.type}
            </div>
          )
      }
    }

    const renderFieldDisplay = () => {
      switch (field.type?.toLowerCase()) {
        case 'text':
        case 'email':
        case 'date':
        case 'color':
          return (
            <span className="text-sm text-gray-900">{value || 'No value'}</span>
          )

        case 'url': {
          const urlValue =
            typeof value === 'object'
              ? value
              : { text: '', url: value || '', target_blank: false }
          if (!urlValue.url && !urlValue.text) {
            return <span className="text-sm text-gray-500">No link set</span>
          }
          return (
            <div className="text-sm">
              <span className="text-gray-900">
                {urlValue.text || urlValue.url}
              </span>
              {urlValue.url && (
                <span className="text-gray-500 ml-2">({urlValue.url})</span>
              )}
              {urlValue.target_blank && (
                <span className="text-xs text-gray-400 ml-2">[new tab]</span>
              )}
            </div>
          )
        }

        case 'textarea':
          return (
            <div className="text-sm text-gray-900">{value || 'No content'}</div>
          )

        case 'number':
          return (
            <span className="text-sm text-gray-900">
              {value !== null && value !== undefined ? value : 'No value'}
            </span>
          )

        case 'boolean':
          return (
            <span className="text-sm text-gray-900">
              {Boolean(value) ? 'Yes' : 'No'}
            </span>
          )

        case 'file': {
          const imageData = typeof value === 'object' ? value : { url: value }
          const imageUrl = getFullImageUrl(imageData.url || imageData)
          if (!imageUrl) {
            return <span className="text-sm text-gray-500">No image</span>
          }
          return (
            <div className="space-y-2">
              <div className="relative">
                <img
                  src={imageUrl}
                  alt={imageData.alt || 'Preview'}
                  className="w-full h-32 object-cover rounded-lg border border-gray-300"
                />
              </div>
              {imageData.alt && (
                <div className="text-xs text-gray-500">
                  Alt: {imageData.alt}
                </div>
              )}
            </div>
          )
        }

        case 'entity selector':
          return (
            <EntitySelector
              field={field}
              value={value}
              onChange={onChange}
              locale={locale}
              readOnly={true}
            />
          )

        case 'form selector':
          return (
            <FormSelector value={value} onChange={onChange} readOnly={true} />
          )

        case 'rich text':
          return (
            <div data-editor-id={`readonly-${uniqueEditorId}`}>
              <IsolatedLexicalEditor
                initialJSON={lexicalValue}
                onChange={() => {}}
                placeholder=""
                editorKey={`readonly-${uniqueEditorId}`}
                readOnly={true}
              />
            </div>
          )

        case 'multiselect':
          return (
            <div className="flex flex-wrap gap-1">
              {(value || []).map((val) => (
                <span
                  key={val}
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                >
                  {field.options?.find((opt) => opt.value === val)?.label ||
                    val}
                </span>
              ))}
            </div>
          )

        case 'select':
          const selectedOption = field.options?.find(
            (opt) => opt.value === value
          )
          return (
            <span className="text-sm text-gray-900">
              {selectedOption?.label || value}
            </span>
          )

        default:
          return (
            <span className="text-sm text-gray-900">
              {typeof value === 'object'
                ? JSON.stringify(value).substring(0, 100) + '...'
                : String(value).substring(0, 100) +
                  (String(value).length > 100 ? '...' : '')}
            </span>
          )
      }
    }

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {isEditable ? renderFieldInput() : renderFieldDisplay()}
        {field.description && (
          <p className="text-xs text-gray-500">{field.description}</p>
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.field.id === nextProps.field.id &&
      prevProps.blockId === nextProps.blockId &&
      prevProps.blockTypeId === nextProps.blockTypeId &&
      prevProps.locale === nextProps.locale &&
      prevProps.isEditable === nextProps.isEditable &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.onChange === nextProps.onChange &&
      JSON.stringify(prevProps.value) === JSON.stringify(nextProps.value)
    )
  }
)

export default InlineFieldEditor
